import { isBefore, subDays } from 'date-fns'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { getServerSession } from 'next-auth'
import { useSession } from 'next-auth/react'
import { useCallback, useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import Image from 'next/image'

import { Alert, Link, Settings } from '@payload-types'

import { authOptions } from '@app/api/auth/[...nextauth]/route'
import { CampaignSlug, getActiveAlertsWhere } from '@collections/Alerts'
import Activities from '@components/activities'
import Alerts, { AlertProps } from '@components/alerts'
import Campaign from '@components/alerts/campaign'
import AppLink from '@components/app-link'
import { NewsPost } from '@components/news/item'
import NewsList from '@components/news/list'
import ButtonLink from '@components/text/button-link'
import Countdown from '@components/text/countdown'
import Title from '@components/text/title'
import { ApiNewsResponseData } from '@pages/api/news/list'
import API_Client from '@services/api-client'
import Blog_Client from '@services/blog-client'
import { AlertsSlug, LinksSlug } from '@utils/collections'
import { MINUTES_IN_MILLISECONDS, NEXT_PUBLIC_REMOVE_FEATURE_VALEU, SECONDS_IN_MILLISECONDS } from '@utils/constants'
import { findDocuments } from '@utils/database'
import { parseDate } from '@utils/dates'
import { runCallbackIntoIntervall } from '@utils/misc'
import { handleAction } from '@utils/router'
import { getSettings } from '@utils/settings'
import { each } from '@utils/objects'
import { ArrowLongRightIcon } from '@heroicons/react/24/solid'

interface IndexPageProps {
  campaign: Alert | null;
  settings: Settings['homepage'] | null;
  links: Link[];
}

IndexPage.auth = true;

export default function IndexPage({ campaign = null, settings = null, links = [] }: IndexPageProps) {
  const { data: session } = useSession();
  const user = session?.user || null;

  const [loadedBlog, setLoadedBlog] = useState(false);
  const [blog, setBlog] = useState<NewsPost[]>([]);

  const [loadedNews, setLoadedNews] = useState(false);
  const [news, setNews] = useState<ApiNewsResponseData>({ pinned: [], last: [] });

  const [loadedNotifications, setLoadedNotifications] = useState(false);
  const [notifications, setNotifications] = useState<AlertProps[]>([]);

  const loadBlog = useCallback(async (limit: number = 3) => {
    const days = settings?.blog_days || 14;
    const modifiedAfter = subDays(new Date(), days).toISOString();
    const response: NewsPost[] = await Blog_Client().getNews({ per_page: limit, modified_after: modifiedAfter });

    setBlog([...response]);
    setLoadedBlog(true);
  }, [settings]);

  const loadNotifications = useCallback(async () => {
    const response = await API_Client().get('notifications/all');

    setLoadedNotifications(true);
    if (!response.success) return;

    setNotifications([...response.data]);
  }, []);

  const loadNews = useCallback(async () => {
    const response = await API_Client().get('news/list', { limit: 3, showToday: true });

    setLoadedNews(true);
    if (! response.success) {
      return;
    }

    const { pinned, last } = response.data as ApiNewsResponseData;
    setNews({ pinned, last });
  }, []);

  useEffect(() => {
    loadBlog();
    loadNews();

    const interval = 5 * MINUTES_IN_MILLISECONDS + 10 * SECONDS_IN_MILLISECONDS;
    return runCallbackIntoIntervall(loadNotifications, interval);
  }, []);

  useEffect(() => {
    if (! blog || ! blog.length) {
      return;
    }

    const { pinned, last } = news;
    const newsCount = pinned.length + last.length;
    if (newsCount > blog.length) {
      loadBlog(newsCount);
    }
  }, [ news, blog ]);

  const totalFortesecrs = settings ? (settings.male_fortesecrs + settings.female_fortesecrs) : 0;
  const maleFortesecrsPercentage = settings ? Math.floor((settings.male_fortesecrs / totalFortesecrs) * 100) : 0;
  const femaleFortesecrsPercentage = settings ? 100 - maleFortesecrsPercentage : 0;

  const closedPositions = settings?.closed_positions ?? [];
  const openPositions = settings?.open_positions ?? [];
  const updatedPositions = settings?.updated_positions ?? [];

  const hasRaioXData = settings && ( totalFortesecrs > 0 || closedPositions.length > 0 || openPositions.length > 0 || updatedPositions.length > 0 );

  return (
    <>
      <Title>Que bom ter você aqui</Title>
      <p>
        Olá, <b>{user?.name || user?.email || 'Fortesecr'}</b>.
      </p>

      <Row className="mt-3 mb-4">
        <Col xs="12">
          <Alerts alerts={notifications} loaded={loadedNotifications} />
        </Col>
      </Row>

      <Row className="mt-3 mb-4">
        <Col xs="12">
          <Activities />
        </Col>
      </Row>

      { isBefore(new Date(), parseDate('2025-12-12 20:00:00', 'yyyy-MM-dd HH:mm:ss') as Date) && (
        <Row className="mt-3 mb-5">
          <Col xs="12">
            <div className="save-the-date-home bg-save-the-date text-center p-4">
              <div className="w-100" onClick={ () => handleAction({ action: '/landing-pages/save-the-date' }) }>
                <Title as="h4" className="m-0 mb-3">Confirme sua presença</Title>
                <Countdown
                  date="2025-12-12 20:00:00"
                  showCompleted={true}
                  showSeconds={false}
                  labels={{
                    days: {
                      singular: 'Dia',
                      plural: 'Dias',
                    },
                    hours: {
                      singular: 'Hora',
                      plural: 'Horas',
                    },
                    minutes: {
                      singular: 'Minuto',
                      plural: 'Minutos',
                    },
                  }}
                >
                  <Title>Chegou o dia!</Title>
                </Countdown>
              </div>
            </div>
          </Col>
        </Row>
      ) }

      <Row className="mt-3 mb-5">
        <Col xs="12" lg="7" className="pe-lg-5">
          {(news.pinned.length > 0 || news.last.length > 0 || ! loadedNews) && (
            <>
              <Title as="h3">Novidades</Title>
              <Row className="mt-2">
                <Col>
                  {news.pinned.length > 0 && <NewsList posts={news.pinned as NewsPost[]} />}
                  {(news.last.length > 0 || !loadedNews) && <NewsList posts={news.last as NewsPost[]} loaded={loadedNews} />}
                </Col>
              </Row>
              { loadedNews && (
                <Row className="mt-2">
                  <Col>
                    <ButtonLink type="primary" action={ '/novidades' } className="w-100">Veja todas as novidades</ButtonLink>
                  </Col>
                </Row>
              ) }
            </>
          )}
        </Col>
        { hasRaioXData && (
          <Col xs="12" lg="5" className="raiox-wrapper">
            <Title as="h3">Raio-X</Title>
            { totalFortesecrs > 0 && (
              <Row className="mt-4 mb-4">
                <Col>
                <Title as="h4" className="text-brand-dark">Atualmente somos</Title>
                  <Row className="mt-4 align-items-center">
                    <Col xs="12" lg="3" className="pe-3">
                      <Image src="/images/homepage/fortesecrs.png" alt="Fortesecrs" width={100} height={100} />
                    </Col>
                    <Col xs="12" lg="9">
                      <Title as="h4" className="text-brand-dark">{ settings.male_fortesecrs + settings.female_fortesecrs } colaboradores</Title>
                      <ul className="mb-0 fortesecrs">
                        { settings.male_fortesecrs && <li>{settings.male_fortesecrs} homens ({maleFortesecrsPercentage}%)</li> }
                        { settings.female_fortesecrs && <li>{settings.female_fortesecrs} mulheres ({femaleFortesecrsPercentage}%)</li> }
                      </ul>
                    </Col>
                  </Row>
                </Col>
              </Row>
            ) }
            { (closedPositions.length > 0 || openPositions.length > 0) && (
              <Row className="mt-4 mb-4 align-items-center">
                <Col xs="12" lg="3" className="pe-3">
                  <Image src="/images/homepage/positions.png" alt="Fortesecrs" width={100} height={100} />
                </Col>
                <Col xs="12" lg="9">
                  { (closedPositions.length > 0) && (
                    <>
                      <Title as="h4" className="text-brand-dark">Vagas Fechadas</Title>
                      <ul className="mb-0">
                        { each(closedPositions, (position, k, index) => (
                          <li key={ position?.id ?? index }>{position.name} - {position.position}</li>
                        ), true)}
                      </ul>
                    </>
                  ) }

                  { (openPositions.length > 0) && (
                    <div className="mt-4">
                      <Title as="h4" className="text-brand-dark">Vagas Abertas</Title>
                      <ul className="mb-0">
                        { each(openPositions, (position, k, index) => (
                          <li key={ position?.id ?? index }>{position.name}</li>
                        ), true)}
                        <li className="list-style-none mt-1">
                          <a href={ 'https://banni.inhire.app/fortesec/vagas' } target="_blank">Veja todas as vagas</a>
                        </li>
                      </ul>
                    </div>
                  ) }
                </Col>
              </Row>
            ) }
            { NEXT_PUBLIC_REMOVE_FEATURE_VALEU !== '1' && (
              <Row className="mt-4 mb-4 align-items-center">
                <Col xs="12" lg="3" className="pe-3">
                  <Image className="rounded-circle" src="/icons/valeu.jpg" alt="Valeu!" width={80} height={80} style={{ marginLeft: '2mm' }} />
                </Col>
                <Col xs="12" lg="9">
                  <Title as="h4" className="text-brand-dark">Valeu!</Title>
                  <ul className="mb-0">
                    <li>Reconheça um colega que fez a diferença.</li>
                    <li className="list-style-none mt-1">
                      <a className="cursor-pointer" onClick={() => handleAction({ action: '/valeu' })}>Acessar o mural</a>
                    </li>
                  </ul>
                </Col>
              </Row>
            ) }
            { (updatedPositions.length > 0) && (
              <Row className="mt-4 mb-4 align-items-center">
                <Col xs="12" lg="3" className="pe-3">
                  <Image src="/images/homepage/movements.png" alt="Fortesecrs" width={100} height={100} />
                </Col>
                <Col xs="12" lg="9">
                  <Title as="h4" className="text-brand-dark">Movimentações</Title>
                  <ul className="mb-0">
                    { each(updatedPositions, (position, k, index) => {
                      if (! position?.old_position) {
                        return <li key={ position?.id ?? index }>{position.name}: {position.new_position}</li>;
                      }

                      return <li key={ position?.id ?? index }>{position.name}: {position.old_position} <ArrowLongRightIcon className="icon-text" /> {position.new_position}</li>;
                    }
                    , true)}
                  </ul>
                </Col>
              </Row>
            ) }
          </Col>
        ) }
      </Row>

      {(blog.length > 0 || ! loadedBlog) && (
        <Row className="mt-3 mb-5">
          <Col xs="12">
            <Title as="h3">Últimas do Blog</Title>
            <Row className="mt-2">
              <Col>
                <NewsList posts={blog} loaded={loadedBlog} />
              </Col>
            </Row>
            { loadedBlog && (
              <Row className="mt-2">
                <Col>
                  <ButtonLink type="primary" action={ 'https://fortesec.com.br/blog' } target="_blank" className="w-100">Conheça o blog da Fortesec</ButtonLink>
                </Col>
              </Row>
            ) }
          </Col>
        </Row>
      )}

      { links.length > 0 && (
        <Row className="mt-3 mb-5">
          <Col>
            <Title as="h3">Links Úteis</Title>
            <Row className="mt-3 row-cols-1 row-cols-sm-2 row-cols-md-4">
              {links.map((link) => (
                <Col key={link.id}>
                  <AppLink
                    name={link.name}
                    description={link.description}
                    href={link.href}
                    color={link.color}
                    textColor={link.textColor ?? undefined}
                  />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      ) }

      <Campaign campaign={campaign} />
    </>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<IndexPageProps>> => {
  const session = await getServerSession( context.req, context.res, authOptions );
  if (! session?.user) {
    return { props: { campaign: null, settings: null, links: [] } };
  }

  const user = session.user;

  const where = {
    and: [
      { type: { equals: CampaignSlug } },
      user ? getActiveAlertsWhere(user) : {},
    ],
  };

  const campaigns = user ? await findDocuments<AlertsSlug>(AlertsSlug, where) : [];
  const campaign = campaigns[0] || null;

  const settings = await getSettings();
  const links = await findDocuments<LinksSlug>(LinksSlug, {}, { sort: 'position' });

  return { props: { campaign, settings: settings?.homepage ?? null, links } };
};
